import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { toast } from 'sonner';

const WebSocketContext = createContext(null);

export const useWebSocket = () => useContext(WebSocketContext);

// Detect backend URL based on hostname
const getBackendUrl = () => {
  // If env variable is set, use it
  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  
  // Production: map frontend domain to backend domain
  const hostname = window.location.hostname;
  
  // Custom domain mapping
  if (hostname === 'cminventario.com' || hostname === 'www.cminventario.com') {
    return 'https://api.cminventario.com';
  }
  
  // Railway preview domain
  if (hostname.includes('railway.app')) {
    return `https://${hostname.replace('frontend', 'backend')}`;
  }
  
  // Local development or fallback
  return '';
};

export const WebSocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const pingIntervalRef = useRef(null);
  const clientId = useRef(`client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`);

  const connect = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    // Use the backend URL detection function
    const backendUrl = getBackendUrl();
    let wsUrl;
    
    if (backendUrl) {
      // Convert http(s) to ws(s). v5.3.8: /api/ws is the new canonical path so
      // the Kubernetes ingress proxies it to the backend pod (everything not
      // prefixed with /api goes to the frontend service and fails).
      wsUrl = backendUrl.replace(/^http/, 'ws') + `/api/ws/${clientId.current}`;
    } else {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      wsUrl = `${protocol}//${window.location.host}/api/ws/${clientId.current}`;
    }
    
    console.log('WebSocket connecting to:', wsUrl);
    
    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log('WebSocket connected');
        setIsConnected(true);
        // Start ping interval
        if (pingIntervalRef.current) {
          clearInterval(pingIntervalRef.current);
        }
        pingIntervalRef.current = setInterval(() => {
          if (wsRef.current?.readyState === WebSocket.OPEN) {
            wsRef.current.send(JSON.stringify({ type: 'ping' }));
          }
        }, 30000);
      };

      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'pong') return;
          
          setLastMessage(data);
          
          // Show toast notifications for important events
          if (data.type === 'movement_created') {
            toast.info(`Nuevo movimiento: ${data.data.movement_type} - ${data.data.event_name || 'Sin evento'}`);
          } else if (data.type === 'unit_updated') {
            toast.info('Inventario actualizado');
          }
        } catch (e) {
          console.error('Error parsing WebSocket message:', e);
        }
      };

      wsRef.current.onclose = () => {
        console.log('WebSocket disconnected');
        setIsConnected(false);
        // Attempt to reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(connect, 5000);
      };

      wsRef.current.onerror = (error) => {
        console.error('WebSocket error:', error);
      };
    } catch (error) {
      console.error('Failed to create WebSocket:', error);
    }
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (pingIntervalRef.current) {
      clearInterval(pingIntervalRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
  }, []);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  const value = {
    isConnected,
    lastMessage,
    reconnect: connect
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
};
